/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountInitialProperties,
  APIAccountResponseInterface,
  RequestAccountInterface,
} from '../../interface/account-management.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/utility-provider';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch accounts
export const fetchAllAccounts = createAsyncThunk<
  APIAccountResponseInterface,
  RequestAccountInterface
>('utility-provider/fetchAllAccounts', async (data: RequestAccountInterface, { dispatch }) => {
  const response = await accountApi
    .fetchAccounts(removeEmptyKeys(data))
    .then((response1) => response1)
    .catch((error) =>
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      ),
    );
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      accountType: 'Utility Provider',
      // status: roleData.submission.status,
      id: roleData._id,
    })),
  };
});

// a thunk to refresh accounts
export const refreshAccounts = createAsyncThunk<
  APIAccountResponseInterface,
  RequestAccountInterface
>('utility-provider/refreshAccounts', async (data: RequestAccountInterface) => {
  const response = await accountApi.fetchAccounts(removeEmptyKeys(data));
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      accountType: 'Utility Provider',
      id: roleData._id,
    })),
  };
});


// a thunk to fetch devices filters
export const fetchUtilityProviderFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('utility-provider/fetchUtilityProviderFilters', async (_, { dispatch }) => {
  try {
    const response = await accountApi.fetchAccountsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountInitialProperties = {
  accounts: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const utilityAccountSlice = createSlice({
  name: 'utility-provider',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing filter update properties.
     */
    updateUtilityProviderFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableUtilityProviderFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing sort field and order.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The builder for handling extra reducers.
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountInitialProperties>) => {
    builder
      .addCase(fetchAllAccounts.pending, (state: AccountInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllAccounts.fulfilled,
        (state: AccountInitialProperties, action: PayloadAction<APIAccountResponseInterface>) => {
          state.loading = false;
          state.accounts = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllAccounts.rejected, (state: AccountInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(
      refreshAccounts.fulfilled,
      (state: AccountInitialProperties, action: PayloadAction<APIAccountResponseInterface>) => {
        state.accounts = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchUtilityProviderFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchUtilityProviderFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchUtilityProviderFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateUtilityProviderFilters, disableUtilityProviderFilterApplied, setSortData } =
  utilityAccountSlice.actions;

// Export the reducer
export default utilityAccountSlice.reducer;
